import React, { useState } from "react";
import { useAuth } from "../contexts/AppKitAuthContext";
import { useScrollLock } from "../hooks/useScrollLock";
import TopNavigation from "../components/TopNavigation";
import styles from "../styles/WelcomeAirdropPage.module.css";

const WelcomeAirdropPage: React.FC = () => {
  const { isAuthenticated, userInfo, isLoading } = useAuth();
  const [copySuccess, setCopySuccess] = useState(false);

  // Lock scroll for the page (not needed since it's a full page, but keeping consistent)
  useScrollLock(false);

  // Check eligibility and participation status
  const isEligible = userInfo?.welcomeAirdropEligible || false;
  const hasParticipated = userInfo?.welcomeAirdropParticipated || false;

  // Copy wallet address to clipboard
  const copyWalletAddress = async () => {
    if (userInfo?.walletAddress) {
      try {
        await navigator.clipboard.writeText(userInfo.walletAddress);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000); // Hide success message after 2 seconds
      } catch (err) {
        console.error('Failed to copy wallet address:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = userInfo.walletAddress;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    }
  };

  return (
    <div className={styles.airdropPage}>
      <TopNavigation fixed />
      <div className={styles.airdropContainer}>
        {/* Header */}
        <div className={styles.airdropHeader}>
          <div className={styles.headerContent}>
            <h1>🎁 Welcome Airdrop</h1>
            <p className={styles.subtitle}>Complete following steps to get your $DXF airdrop</p>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.airdropContent}>
          {!isAuthenticated ? (
            // Not signed in state
            <div className={styles.notSignedIn}>
              <h3>🔒 Sign In Required</h3>
              <p>Connect your wallet to check your welcome airdrop eligibility.</p>
            </div>
          ) : isLoading ? (
            <div className={styles.loading}>Loading eligibility status...</div>
          ) : !isEligible ? (
            // Not eligible state
            <div className={styles.notEligible}>
              <h3>❌ Not Eligible</h3>
              <p>Welcome airdrop is only available for users who registered within the last 3 days.</p>
              <p>This promotion has expired for your account.</p>
            </div>
          ) : hasParticipated ? (
            // Already participated state
            <div className={styles.alreadyParticipated}>
              <h3>✅ Already Participated</h3>
              <p>You have already participated in the welcome airdrop.</p>
              <p>Your $DXF tokens will be in your wallet within 3 work days!</p>
            </div>
          ) : (
            // Eligible state - show steps
            <div className={styles.stepsSection}>
              <div className={styles.step}>
                <div className={styles.stepNumber}>1</div>
                <div className={styles.stepContent}>
                  <h3>
                    Follow us on X{" "}
                    <a
                      href="https://x.com/intent/follow?screen_name=ProofXGames"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      @ProofXGames
                    </a>
                  </h3>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>2</div>
                <div className={styles.stepContent}>
                  <h3>
                    Like and Retweet{" "}
                    <a
                      href="https://x.com/intent/retweet?tweet_id=1962307178431414578"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      this tweet post
                    </a>
                  </h3>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>3</div>
                <div className={styles.stepContent}>
                  <h3>
                    Join our community on Telegram{" "}
                    <a
                      href="https://t.me/DXFCommunity"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.socialLink}
                    >
                      @DXFCommunity
                    </a>
                  </h3>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>4</div>
                <div className={styles.stepContent}>
                  <h3>
                    Send the screenshot of your retweet together with your <strong>X username</strong> and <strong>wallet address</strong> to the community group on
                    Telegram
                  </h3>
                  <div className={styles.walletInfo}>
                    <p>Your wallet address:</p>
                    <div className={styles.walletAddressContainer}>
                      <code className={styles.walletAddress}>{userInfo?.walletAddress}</code>
                      <button 
                        onClick={copyWalletAddress}
                        className={styles.copyButton}
                        title="Copy wallet address"
                      >
                        {copySuccess ? '✓' : '📋'}
                      </button>
                    </div>
                    {copySuccess && (
                      <div className={styles.copySuccessMessage}>
                        Copied to clipboard!
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.step}>
                <div className={styles.stepNumber}>5</div>
                <div className={styles.stepContent}>
                  <h3>Wait for our mod to review. Your $DXF will in your wallet within 3 work days!</h3>
                  <p className={styles.note}>
                    Our moderators will verify your submission and process the airdrop. Please be patient as we review
                    all submissions manually to ensure fairness.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeAirdropPage;
